import { useCallback, useEffect, useRef, useState } from 'react';
import { goi, LoiApi, tien } from '../chung/api';
import { useHoiLai } from '../chung/hoi-lai';
import type { DongSoCaiKho, NhomSanPham, SanPham, TrangSanPham } from '../chung/kieu';
import { AnhSanPham, MaDoiChieu } from '../chung/AnhSanPham';
import { DaiLoi, DaiTot, HopHoi, Khung, O } from './manh';

/**
 * Sản phẩm — tìm, sửa, tạo, nạp Excel, xem sổ cái kho.
 *
 * 🔴 Đổi GIÁ là đổi tiền khách trả. Nên hộp hỏi lại nói rõ **giá cũ → giá mới**,
 *    không hỏi chung chung. Giá đã chụp vào từng dòng giỏ lúc thêm, nên đổi ở
 *    đây không đụng giỏ đang mở — câu đó cũng phải hiện ra để quản lý yên tâm.
 */
export function MucSanPham() {
  const [q, datQ] = useState('');
  const [nhomId, datNhomId] = useState<number | null>(null);
  const [caNgung, datCaNgung] = useState(false);
  const [kq, datKq] = useState<TrangSanPham | null>(null);
  const [dangTim, datDangTim] = useState(true);
  const [loi, datLoi] = useState<string | null>(null);
  const [tot, datTot] = useState<string | null>(null);
  const [sua, datSua] = useState<SanPham | null>(null);
  const [taoMoi, datTaoMoi] = useState(false);
  const [soCai, datSoCai] = useState<SanPham | null>(null);
  const [nhipTai, datNhipTai] = useState(0);

  const nhom = useHoiLai<NhomSanPham[]>('/api/nhom', 60_000);

  useEffect(() => {
    let con = true;
    datDangTim(true);
    const h = setTimeout(async () => {
      try {
        const ts = new URLSearchParams();
        if (q.trim()) ts.set('q', q.trim());
        if (nhomId !== null) ts.set('nhom_id', String(nhomId));
        if (caNgung) ts.set('ca_hang_ngung', 'true');
        const d = await goi<TrangSanPham>(`/api/san-pham?${ts}`);
        if (con) datKq(d);
      } catch (e) {
        if (con) datLoi(e instanceof LoiApi ? e.message : 'Không tìm được.');
      } finally {
        if (con) datDangTim(false);
      }
    }, 250);
    return () => {
      con = false;
      clearTimeout(h);
    };
  }, [q, nhomId, caNgung, nhipTai]);

  const taiLai = () => datNhipTai((c) => c + 1);

  return (
    <Khung
      tieuDe="Sản phẩm"
      phu={
        kq
          ? `${kq.tong} mặt hàng${caNgung ? ' (kể cả đã ngừng bán)' : ' đang bán'}`
          : 'Đang tải…'
      }
      hanhDong={
        <button className="nut-chinh" onClick={() => datTaoMoi(true)}>
          + Thêm sản phẩm
        </button>
      }
    >
      <DaiLoi loi={loi} />
      <DaiTot chu={tot} />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <input
          value={q}
          onChange={(e) => datQ(e.target.value)}
          placeholder="Tên hoặc mã — gõ không dấu cũng ra"
          autoCapitalize="none"
          spellCheck={false}
          style={{ flex: 1 }}
        />
        <button
          onClick={() => datCaNgung((c) => !c)}
          className={caNgung ? 'lom-nho' : undefined}
          style={{ whiteSpace: 'nowrap', color: caNgung ? 'var(--nhan)' : undefined }}
        >
          Đóng cửa hàng
        </button>
      </div>

      <div style={{ display: 'flex', gap: 9, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          onClick={() => datNhomId(null)}
          className={nhomId === null ? 'lom-nho' : undefined}
          style={{ ...chip, color: nhomId === null ? 'var(--nhan)' : undefined }}
        >
          Tất cả nhóm
        </button>
        {(nhom.duLieu ?? []).map((n) => (
          <button
            key={n.id}
            onClick={() => datNhomId((c) => (c === n.id ? null : n.id))}
            className={nhomId === n.id ? 'lom-nho' : undefined}
            style={{ ...chip, color: nhomId === n.id ? 'var(--nhan)' : undefined }}
          >
            {n.name}
          </button>
        ))}
      </div>

      {dangTim && !kq && (
        <>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="xuong" style={{ height: 64, marginBottom: 12 }} />
          ))}
        </>
      )}

      {kq?.danh_sach.length === 0 && (
        <div style={{ padding: '40px 10px', textAlign: 'center', color: 'var(--chu-3)' }}>
          Không có mặt hàng nào khớp.
        </div>
      )}

      {(kq?.danh_sach ?? []).map((sp, i) => (
        <div
          key={sp.id}
          className="noi-nho truot-phai"
          style={{
            animationDelay: `${Math.min(i, 8) * 30}ms`,
            padding: '14px 18px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <AnhSanPham anh={sp.anh} ten={sp.ten} co={52} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 600 }}>{sp.ten}</span>
              {!sp.active && <span className="the the-mo">Đã ngừng bán</span>}
              {sp.het_hang && <span className="the the-nhan">Hết hàng</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--chu-3)', marginTop: 4 }}>
              {sp.sku}
              {sp.nhom && ` · ${sp.nhom}`} · còn bán {sp.con_ban} {sp.don_vi}
            </div>
            <MaDoiChieu sapcode={sp.sapcode} barcode={sp.barcode} />
          </div>
          <span className="tien" style={{ fontSize: 'var(--chu-to)', flexShrink: 0 }}>
            {tien(sp.gia)}
          </span>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => datSoCai(sp)} style={{ padding: '9px 13px', fontSize: 13 }}>
              Sổ kho
            </button>
            <button onClick={() => datSua(sp)} style={{ padding: '9px 13px', fontSize: 13 }}>
              Sửa
            </button>
          </div>
        </div>
      ))}

      {sua && (
        <HopSua
          sp={sua}
          nhom={nhom.duLieu ?? []}
          taiLaiNhom={nhom.taiLai}
          dong={() => datSua(null)}
          xong={(c) => {
            datSua(null);
            datTot(c);
            taiLai();
          }}
        />
      )}
      {taoMoi && (
        <HopTao
          nhom={nhom.duLieu ?? []}
          taiLaiNhom={nhom.taiLai}
          dong={() => datTaoMoi(false)}
          xong={(c) => {
            datTaoMoi(false);
            datTot(c);
            taiLai();
          }}
        />
      )}
      {soCai && <HopSoCai sp={soCai} dong={() => datSoCai(null)} />}
    </Khung>
  );
}

const chip: React.CSSProperties = {
  padding: '8px 15px',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 'var(--tron)',
  whiteSpace: 'nowrap',
};

// ───────────────────────── chọn nhóm ─────────────────────────

/**
 * Giá trị của <option> mở hộp quản lý nhóm.
 *
 * 🔴 Phải là chuỗi KHÔNG BAO GIỜ trùng một id thật. `<select>` chỉ trả về chuỗi,
 *    nên một giá trị như "0" hay "-1" sẽ đụng ngay khi ai đó đổi kiểu khoá.
 */
const QUAN_LY_NHOM = '__quan-ly-nhom__';

/**
 * Ô chọn nhóm, kèm dòng cuối mở hộp quản lý nhóm.
 *
 * Dùng chung cho cả hộp THÊM lẫn hộp SỬA sản phẩm — hai chỗ này trước đây chép
 * nguyên một khối <select> giống hệt nhau, và đó đúng là kiểu chỗ sẽ lệch nhau
 * sau vài lần sửa.
 */
function ONhom({
  nhomId,
  datNhomId,
  nhom,
  taiLaiNhom,
}: {
  nhomId: string;
  datNhomId: (v: string) => void;
  nhom: NhomSanPham[];
  taiLaiNhom: () => void;
}) {
  const [hoi, datHoi] = useState(false);

  /*
    🔴 Nhớ tại chỗ mọi nhóm vừa tạo hoặc vừa đổi tên, cho tới khi danh sách của
       màn cha tải lại.

       Danh sách nhóm đi qua `useHoiLai`, mà `taiLaiNhom()` là bất đồng bộ. Trỏ
       `<select>` vào một id chưa có trong danh sách thì ô hiện TRỐNG — người
       dùng vừa tạo nhóm xong đã tưởng là hỏng, rồi tạo lại lần nữa. Đúng cái
       vòng lặp sinh ra nhóm rác.
  */
  const [biet, datBiet] = useState<Record<number, NhomSanPham>>({});

  const ds: NhomSanPham[] = nhom.map((n) => biet[n.id] ?? n);
  for (const n of Object.values(biet)) {
    if (ds.some((x) => x.id === n.id)) continue;
    // Nhóm đã tắt vẫn phải có mặt NẾU sản phẩm này đang thuộc về nó — không thì
    // mở hộp sửa ra là thấy ô nhóm trống, và lưu lại là mất nhóm mà không hay.
    if (n.active || String(n.id) === nhomId) ds.push(n);
  }
  ds.sort((a, b) => a.name.localeCompare(b.name, 'vi'));

  return (
    <>
      <O nhan="Nhóm">
        <select
          value={nhomId}
          onChange={(e) => {
            if (e.target.value === QUAN_LY_NHOM) {
              // KHÔNG đổi nhóm đang chọn: bấm nhầm rồi đóng hộp thì phải còn
              // nguyên nhóm cũ, không được rơi về "(không nhóm)".
              datHoi(true);
              return;
            }
            datNhomId(e.target.value);
          }}
        >
          <option value="">(không nhóm)</option>
          {ds.map((n) => (
            <option key={n.id} value={n.id}>
              {n.name}
              {n.active ? '' : ' — đã tắt'}
            </option>
          ))}
          <option value={QUAN_LY_NHOM}>+ Thêm · sửa nhóm…</option>
        </select>
      </O>

      {hoi && (
        <HopQuanLyNhom
          dong={() => datHoi(false)}
          daTao={(n) => {
            datBiet((c) => ({ ...c, [n.id]: n }));
            datNhomId(String(n.id));
            taiLaiNhom();
          }}
          daSua={(n) => {
            datBiet((c) => ({ ...c, [n.id]: n }));
            taiLaiNhom();
          }}
        />
      )}
    </>
  );
}

/**
 * Dòng phụ dưới tên nhóm: tạo lúc nào, sửa lần cuối lúc nào.
 *
 * Chỉ hiện "sửa" khi nó KHÁC ngày tạo — nhóm chưa ai đụng tới thì hai mốc trùng
 * nhau, in cả hai chỉ thành hai con số giống hệt nằm cạnh nhau, đọc mất công mà
 * không nói thêm được gì.
 */
function moc(n: NhomSanPham) {
  const ngay = (t: string) => new Date(t).toLocaleDateString('vi-VN');
  const tao = ngay(n.createdAt);
  const sua = ngay(n.updatedAt);
  // So theo GIÂY chứ không theo chuỗi ngày: sửa trong cùng ngày vẫn phải hiện
  // giờ, không thì quản lý sửa xong nhìn lại tưởng chưa lưu.
  const daSua = new Date(n.updatedAt).getTime() - new Date(n.createdAt).getTime() > 1000;
  if (!daSua) return `Tạo ${tao}`;
  const gio = new Date(n.updatedAt).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit' });
  return `Tạo ${tao} · sửa ${sua === tao ? gio : sua}`;
}

/**
 * Hộp quản lý nhóm — lồng bên trong hộp thêm/sửa sản phẩm.
 *
 * 🔴 KHÔNG có xoá. Cố ý, và ràng buộc khoá ngoại là lý do:
 *    `products.group_id` và `discount_targets.group_id` đều `ON DELETE SET NULL`.
 *    Xoá một nhóm sẽ ÂM THẦM đẩy mọi sản phẩm của nó về "không nhóm", và làm một
 *    chương trình giảm giá đang nhắm nhóm đó mất mục tiêu mà vẫn chạy. Không có
 *    lỗi nào báo ra. Tắt nhóm đi thì nhóm biến khỏi mọi bộ lọc, còn sản phẩm và
 *    giảm giá không suy suyển — đó mới là thứ người ta thật sự muốn khi nói "bỏ
 *    nhóm này đi".
 *
 * 🔴 Hộp này hỏi `?ca_hang_ngung=true` để lấy CẢ nhóm đã tắt.
 *    `/api/nhom` mặc định chỉ trả nhóm đang bật. Lấy danh sách mặc định thì nhóm
 *    vừa tắt biến mất khỏi đây luôn, và không còn đường nào bật lại.
 */
function HopQuanLyNhom({
  dong,
  daTao,
  daSua,
}: {
  dong: () => void;
  daTao: (n: NhomSanPham) => void;
  daSua: (n: NhomSanPham) => void;
}) {
  const [ds, datDs] = useState<NhomSanPham[] | null>(null);
  const [loi, datLoi] = useState<string | null>(null);

  const [tenMoi, datTenMoi] = useState('');
  const [dangTao, datDangTao] = useState(false);

  const [suaId, datSuaId] = useState<number | null>(null);
  const [tenSua, datTenSua] = useState('');
  const [activeSua, datActiveSua] = useState(true);
  const [dangLuu, datDangLuu] = useState(false);

  const tai = useCallback(async () => {
    try {
      datDs(await goi<NhomSanPham[]>('/api/nhom?ca_hang_ngung=true'));
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không đọc được danh sách nhóm.');
    }
  }, []);

  useEffect(() => {
    void tai();
  }, [tai]);

  /*
    🔴 So theo CHỮ THƯỜNG, và chặn ngay tại ô nhập.

       Server kiểm trùng bằng `where: { name }` — Postgres so phân biệt hoa
       thường — nên "Nước hoa" và "nước hoa" lọt thành HAI nhóm. Đường nạp Excel
       thì lại gộp chúng làm một. Đã xảy ra thật: một lần gõ nhầm chữ hoa đẻ ra
       nhóm trùng, mà không có đường xoá.
  */
  const trung = (t: string, boQua?: number) =>
    (ds ?? []).some(
      (n) => n.id !== boQua && n.name.trim().toLowerCase() === t.trim().toLowerCase(),
    );

  const moi = tenMoi.trim();
  const moiHopLe = moi.length >= 1 && moi.length <= 100 && !trung(moi);

  const tao = async () => {
    if (!moiHopLe || dangTao) return;
    datDangTao(true);
    datLoi(null);
    try {
      const n = await goi<NhomSanPham>('/api/nhom', { method: 'POST', than: { ten: moi } });
      datTenMoi('');
      daTao(n);
      await tai();
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không tạo được nhóm.');
    } finally {
      datDangTao(false);
    }
  };

  const sua = tenSua.trim();
  const suaHopLe = sua.length >= 1 && sua.length <= 100 && !trung(sua, suaId ?? undefined);

  const luu = async () => {
    if (suaId === null || !suaHopLe || dangLuu) return;
    datDangLuu(true);
    datLoi(null);
    try {
      const n = await goi<NhomSanPham>(`/api/nhom/${suaId}`, {
        method: 'PATCH',
        than: { ten: sua, active: activeSua },
      });
      daSua(n);
      datSuaId(null);
      await tai();
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không lưu được.');
    } finally {
      datDangLuu(false);
    }
  };

  /*
    🔴 Kéo dòng đang sửa vào tầm nhìn.

       Bấm "Sửa" ở dòng gần cuối thì form bung ra cao thêm ~150px và hai nút
       LƯU / HUỶ rơi xuống dưới mép vùng cuộn — nhìn ảnh chụp mới thấy, đọc code
       thì không. Cùng một hình dạng với lỗi nút TẠO của hộp giảm giá nằm dưới
       mép màn hình 900px (đã ghi ở `soi-man-hinh.cjs`).
  */
  const oDangSua = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (suaId !== null) oDangSua.current?.scrollIntoView({ block: 'center' });
  }, [suaId]);

  const moSua = (n: NhomSanPham) => {
    datLoi(null);
    datSuaId(n.id);
    datTenSua(n.name);
    datActiveSua(n.active);
  };

  return (
    <HopHoi
      tieuDe="Nhóm sản phẩm"
      nutY="XONG"
      dangLam={false}
      dong={dong}
      dongY={dong}
      anQuayLai
    >
      <DaiLoi loi={loi} />

      {/* ── thêm mới ── */}
      <div className="lom-nho" style={{ padding: '14px 16px', marginBottom: 18 }}>
        <span className="nhan-chu">Thêm nhóm mới</span>
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <input
            value={tenMoi}
            onChange={(e) => datTenMoi(e.target.value)}
            placeholder="Nước hoa"
            style={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void tao();
              }
            }}
          />
          <button
            className="nut-chinh"
            style={{ padding: '0 20px', flexShrink: 0 }}
            disabled={!moiHopLe || dangTao}
            onClick={() => void tao()}
          >
            {dangTao ? '…' : 'THÊM'}
          </button>
        </div>
        {moi !== '' && trung(moi) && (
          <div style={{ color: 'var(--nhan)', fontWeight: 600, fontSize: 12, marginTop: 9 }}>
            Đã có nhóm tên này rồi — sửa nhóm cũ ở dưới thay vì tạo thêm.
          </div>
        )}
        {moi.length > 100 && (
          <div style={{ color: 'var(--nhan)', fontWeight: 600, fontSize: 12, marginTop: 9 }}>
            Tên dài {moi.length} ký tự — tối đa 100.
          </div>
        )}
      </div>

      {/* ── danh sách ── */}
      <div className="nhan-chu" style={{ margin: '0 4px 10px' }}>
        {ds ? `${ds.length} nhóm` : 'Đang tải…'}
      </div>

      {!ds && [0, 1, 2].map((i) => <div key={i} className="xuong" style={{ height: 48, marginBottom: 10 }} />)}

      {(ds ?? []).map((n) =>
        suaId === n.id ? (
          <div
            key={n.id}
            ref={oDangSua}
            className="lom-nho"
            style={{ padding: '13px 15px', marginBottom: 10 }}
          >
            <input
              value={tenSua}
              onChange={(e) => datTenSua(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void luu();
                }
              }}
            />
            {/*
              Bật/tắt nằm TRONG form sửa, không đứng riêng thành nút ở mỗi dòng:
              tắt một nhóm là thứ hiếm khi làm, mà để nút ngay cạnh "Sửa" thì sẽ
              có người bấm nhầm — và nhóm biến khỏi màn nhân viên mà không ai hiểu
              vì sao.
            */}
            <button
              onClick={() => datActiveSua((c) => !c)}
              className={activeSua ? undefined : 'lom-nho'}
              style={{
                width: '100%',
                padding: 11,
                marginTop: 10,
                fontSize: 13,
                color: activeSua ? undefined : 'var(--nhan)',
              }}
            >
              {activeSua ? 'Đang dùng — bấm để TẮT nhóm' : 'Đã tắt — bấm để dùng lại'}
            </button>
            {!activeSua && (
              <div style={{ fontSize: 12, color: 'var(--chu-3)', marginTop: 8, lineHeight: 1.6 }}>
                Tắt thì nhóm biến khỏi bộ lọc của màn tư vấn và khỏi ô chọn ở đây.{' '}
                <b>Sản phẩm trong nhóm không suy suyển</b> — vẫn bán, vẫn tìm được bằng tên.
              </div>
            )}
            {sua !== '' && trung(sua, n.id) && (
              <div style={{ color: 'var(--nhan)', fontWeight: 600, fontSize: 12, marginTop: 9 }}>
                Trùng tên với một nhóm khác.
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button style={{ flex: 1, padding: 11 }} onClick={() => datSuaId(null)} disabled={dangLuu}>
                Huỷ
              </button>
              <button
                className="nut-chinh"
                style={{ flex: 1, padding: 11 }}
                disabled={!suaHopLe || dangLuu}
                onClick={() => void luu()}
              >
                {dangLuu ? 'Đang lưu…' : 'LƯU'}
              </button>
            </div>
          </div>
        ) : (
          <div
            key={n.id}
            className="noi-nho"
            style={{
              padding: '12px 15px',
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: n.active ? undefined : 'var(--chu-3)' }}>
                {n.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--chu-3)', marginTop: 3 }}>{moc(n)}</div>
            </div>
            {!n.active && <span className="the the-mo">Đã tắt</span>}
            <button
              onClick={() => moSua(n)}
              style={{ padding: '8px 14px', fontSize: 13, flexShrink: 0 }}
            >
              Sửa
            </button>
          </div>
        ),
      )}

      <div style={{ fontSize: 12, color: 'var(--chu-3)', lineHeight: 1.65, marginTop: 6 }}>
        <b>Không xoá được nhóm</b> — sản phẩm gắn với nhóm, xoá cứng thì chúng mất nhóm mà
        không có gì báo. Gõ nhầm thì <b>sửa tên</b>; không dùng nữa thì <b>tắt đi</b>.
      </div>
    </HopHoi>
  );
}

// ───────────────────────── sửa ─────────────────────────

function HopSua({
  sp,
  nhom,
  taiLaiNhom,
  dong,
  xong,
}: {
  sp: SanPham;
  nhom: NhomSanPham[];
  taiLaiNhom: () => void;
  dong: () => void;
  xong: (chu: string) => void;
}) {
  const [ten, datTen] = useState(sp.ten);
  const [gia, datGia] = useState(String(sp.gia));
  const [donVi, datDonVi] = useState(sp.don_vi);
  const [nhomId, datNhomId] = useState<string>(sp.nhom_id === null ? '' : String(sp.nhom_id));
  const [active, datActive] = useState(sp.active);
  const [sapcode, datSapcode] = useState(sp.sapcode ?? '');
  const [barcode, datBarcode] = useState(sp.barcode ?? '');
  // Ô nhập giữ đường dẫn TƯƠNG ĐỐI (`product-images/x.jpg`), không phải `/media/...`
  // mà API trả về cho <img> — hai thứ khác nhau, đổi qua lại ở đúng một chỗ này.
  const [anh, datAnh] = useState(sp.anh ? decodeURI(sp.anh.replace(/^\/media\//, '')) : '');
  const [dangLam, datDangLam] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);

  const giaMoi = Number(gia.replace(/[.\s]/g, ''));
  const giaHopLe = gia.trim() !== '' && Number.isInteger(giaMoi) && giaMoi > 0;
  const doiGia = giaHopLe && giaMoi !== sp.gia;
  const barcodeHopLe = barcode.trim() === '' || /^\d{8,14}$/.test(barcode.trim());

  const luu = async () => {
    if (!giaHopLe || dangLam) return;
    datDangLam(true);
    datLoi(null);
    try {
      await goi(`/api/san-pham/${sp.id}`, {
        method: 'PATCH',
        than: {
          ten: ten.trim(),
          gia: giaMoi,
          don_vi: donVi.trim() || 'cái',
          nhom_id: nhomId === '' ? null : Number(nhomId),
          active,
          // Gửi rỗng = XOÁ. Đây là chỗ duy nhất gỡ được mã vạch/ảnh — trình nạp
          // Excel coi ô trống là giữ nguyên.
          sapcode: sapcode.trim(),
          barcode: barcode.trim(),
          anh: anh.trim(),
        },
      });
      xong(`Đã lưu "${ten.trim()}".`);
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không lưu được.');
      datDangLam(false);
    }
  };

  return (
    <HopHoi
      tieuDe={`Sửa ${sp.sku}`}
      nutY={doiGia ? 'ĐỔI GIÁ VÀ LƯU' : 'Lưu'}
      dangLam={dangLam}
      dong={dong}
      dongY={() => void luu()}
      choPhep={giaHopLe && barcodeHopLe && ten.trim() !== ''}
    >
      <DaiLoi loi={loi} />
      <O nhan="Tên sản phẩm">
        <input value={ten} onChange={(e) => datTen(e.target.value)} />
      </O>
      <div style={{ display: 'flex', gap: 14 }}>
        <div style={{ flex: 2 }}>
          <O nhan="Giá bán (đồng)">
            <input
              value={gia}
              onChange={(e) => datGia(e.target.value)}
              inputMode="numeric"
              className="so"
            />
          </O>
        </div>
        <div style={{ flex: 1 }}>
          <O nhan="Đơn vị">
            <input value={donVi} onChange={(e) => datDonVi(e.target.value)} />
          </O>
        </div>
      </div>
      <ONhom nhomId={nhomId} datNhomId={datNhomId} nhom={nhom} taiLaiNhom={taiLaiNhom} />
      <OMaDoiChieu
        sapcode={sapcode}
        datSapcode={datSapcode}
        barcode={barcode}
        datBarcode={datBarcode}
        anh={anh}
        datAnh={datAnh}
        barcodeHopLe={barcodeHopLe}
      />

      <button
        onClick={() => datActive((c) => !c)}
        className={active ? undefined : 'lom-nho'}
        style={{ width: '100%', padding: 13, marginBottom: 16, color: active ? undefined : 'var(--nhan)' }}
      >
        {active ? 'Đang bán — bấm để NGỪNG BÁN' : 'Đã ngừng bán — bấm để bán lại'}
      </button>

      {!giaHopLe && (
        <div style={{ color: 'var(--nhan)', fontWeight: 600, fontSize: 'var(--chu-nho)' }}>
          Giá phải là số nguyên dương, tính bằng đồng.
        </div>
      )}

      {/*
        🔴 Nói RÕ giá cũ → giá mới, không hỏi "chắc chưa?".
           Đây là tiền khách trả; quản lý phải đọc được con số trước khi bấm.
      */}
      {doiGia && (
        <div className="lom-nho" style={{ padding: '14px 16px' }}>
          <div style={{ fontWeight: 700, color: 'var(--nhan)', marginBottom: 6 }}>Giá sẽ đổi</div>
          <div className="so" style={{ fontSize: 17 }}>
            {tien(sp.gia)} <span style={{ color: 'var(--chu-3)' }}>→</span>{' '}
            <span className="tien">{tien(giaMoi)}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--chu-2)', marginTop: 8, lineHeight: 1.6 }}>
            Giỏ đang mở <b>không đổi</b> — giá đã chụp vào từng dòng lúc nhân viên thêm hàng.
          </div>
        </div>
      )}
    </HopHoi>
  );
}

// ───────────────────────── mã hãng · mã vạch · ảnh ─────────────────────────

/**
 * Ba ô cho khách đối chiếu (từ 14/09), dùng chung cho hộp Sửa và hộp Thêm.
 * Không bắt buộc ô nào. Mã vạch kiểm ngay ở đây (8–14 chữ số) để nút Lưu tắt
 * trước khi gửi — server vẫn kiểm lại, đây chỉ là đỡ một vòng đi về.
 */
function OMaDoiChieu({
  sapcode,
  datSapcode,
  barcode,
  datBarcode,
  anh,
  datAnh,
  barcodeHopLe,
}: {
  sapcode: string;
  datSapcode: (v: string) => void;
  barcode: string;
  datBarcode: (v: string) => void;
  anh: string;
  datAnh: (v: string) => void;
  barcodeHopLe: boolean;
}) {
  return (
    <>
      <div style={{ display: 'flex', gap: 14 }}>
        <div style={{ flex: 1 }}>
          <O nhan="Mã hãng (SAP)">
            <input
              value={sapcode}
              onChange={(e) => datSapcode(e.target.value)}
              placeholder="G4823200"
              spellCheck={false}
              className="so"
            />
          </O>
        </div>
        <div style={{ flex: 1 }}>
          <O nhan="Mã vạch">
            <input
              value={barcode}
              onChange={(e) => datBarcode(e.target.value)}
              placeholder="6902395979012"
              inputMode="numeric"
              spellCheck={false}
              className="so"
            />
          </O>
        </div>
      </div>
      {!barcodeHopLe && (
        <div style={{ color: 'var(--nhan)', fontWeight: 600, fontSize: 'var(--chu-nho)', marginTop: -8, marginBottom: 12 }}>
          Mã vạch chỉ gồm 8 đến 14 chữ số.
        </div>
      )}
      <O nhan="Ảnh — tên file trong thư mục product-images">
        <input
          value={anh}
          onChange={(e) => datAnh(e.target.value)}
          placeholder="product-images/1_VelvetPlush.jpg"
          spellCheck={false}
        />
      </O>
    </>
  );
}

// ───────────────────────── tạo mới ─────────────────────────

function HopTao({
  nhom,
  taiLaiNhom,
  dong,
  xong,
}: {
  nhom: NhomSanPham[];
  taiLaiNhom: () => void;
  dong: () => void;
  xong: (chu: string) => void;
}) {
  const [sku, datSku] = useState('');
  const [ten, datTen] = useState('');
  const [gia, datGia] = useState('');
  const [donVi, datDonVi] = useState('cái');
  const [nhomId, datNhomId] = useState('');
  const [tonDau, datTonDau] = useState('0');
  const [sapcode, datSapcode] = useState('');
  const [barcode, datBarcode] = useState('');
  const [anh, datAnh] = useState('');
  const [dangLam, datDangLam] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);
  const barcodeHopLe = barcode.trim() === '' || /^\d{8,14}$/.test(barcode.trim());

  const giaSo = Number(gia.replace(/[.\s]/g, ''));
  // 🔴 `Number('') === 0` — không kiểm chuỗi rỗng thì hàng giá 0 lọt vào im lặng.
  //    Lỗi này đã suýt lọt vào file mẫu gửi cho nhân viên.
  const giaHopLe = gia.trim() !== '' && Number.isInteger(giaSo) && giaSo > 0;
  const tonSo = Number(tonDau.replace(/[.\s]/g, '') || '0');
  const tonHopLe = Number.isInteger(tonSo) && tonSo >= 0;

  const tao = async () => {
    if (!giaHopLe || !tonHopLe || !barcodeHopLe || !ten.trim() || dangLam) return;
    datDangLam(true);
    datLoi(null);
    try {
      await goi('/api/san-pham', {
        method: 'POST',
        than: {
          ...(sku.trim() ? { sku: sku.trim() } : {}),
          ten: ten.trim(),
          gia: giaSo,
          don_vi: donVi.trim() || 'cái',
          ...(nhomId ? { nhom_id: Number(nhomId) } : {}),
          ton_dau: tonSo,
          ...(sapcode.trim() ? { sapcode: sapcode.trim() } : {}),
          ...(barcode.trim() ? { barcode: barcode.trim() } : {}),
          ...(anh.trim() ? { anh: anh.trim() } : {}),
        },
      });
      xong(`Đã thêm "${ten.trim()}".`);
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không tạo được.');
      datDangLam(false);
    }
  };

  return (
    <HopHoi
      tieuDe="Thêm sản phẩm"
      nutY="THÊM"
      dangLam={dangLam}
      dong={dong}
      dongY={() => void tao()}
      choPhep={giaHopLe && tonHopLe && barcodeHopLe && ten.trim() !== ''}
    >
      <DaiLoi loi={loi} />
      <O nhan="Mã sản phẩm — để trống thì hệ thống tự đặt">
        <input value={sku} onChange={(e) => datSku(e.target.value)} placeholder="SON-09" spellCheck={false} />
      </O>
      <O nhan="Tên sản phẩm ★">
        <input value={ten} onChange={(e) => datTen(e.target.value)} />
      </O>
      <div style={{ display: 'flex', gap: 14 }}>
        <div style={{ flex: 2 }}>
          <O nhan="Giá bán (đồng) ★">
            <input value={gia} onChange={(e) => datGia(e.target.value)} inputMode="numeric" className="so" />
          </O>
        </div>
        <div style={{ flex: 1 }}>
          <O nhan="Đơn vị">
            <input value={donVi} onChange={(e) => datDonVi(e.target.value)} />
          </O>
        </div>
      </div>
      <ONhom nhomId={nhomId} datNhomId={datNhomId} nhom={nhom} taiLaiNhom={taiLaiNhom} />
      <O nhan="Số lượng">
        <input value={tonDau} onChange={(e) => datTonDau(e.target.value)} inputMode="numeric" className="so" />
      </O>
      <OMaDoiChieu
        sapcode={sapcode}
        datSapcode={datSapcode}
        barcode={barcode}
        datBarcode={datBarcode}
        anh={anh}
        datAnh={datAnh}
        barcodeHopLe={barcodeHopLe}
      />
      {!giaHopLe && gia.trim() !== '' && (
        <div style={{ color: 'var(--nhan)', fontWeight: 600, fontSize: 'var(--chu-nho)' }}>
          Giá phải là số nguyên dương.
        </div>
      )}
    </HopHoi>
  );
}

// ───────────────────────── sổ cái kho ─────────────────────────

const TEN_LOAI: Record<string, string> = {
  NHAP_HANG: 'Nhập hàng',
  KIEM_KE: 'Kiểm kê',
  GIU: 'Giữ cho giỏ',
  NHA_GIU: 'Nhả về kho',
  BAN: 'Bán ra',
  TRA_LAI: 'Trả lại',
};

/** *"Vì sao còn từng này"* — đọc từ dưới lên là ra cả đời của mặt hàng. */
function HopSoCai({ sp, dong }: { sp: SanPham; dong: () => void }) {
  const [ds, datDs] = useState<DongSoCaiKho[] | null>(null);
  const [loi, datLoi] = useState<string | null>(null);

  useEffect(() => {
    let con = true;
    goi<DongSoCaiKho[]>(`/api/san-pham/${sp.id}/so-cai-kho`)
      .then((d) => con && datDs(d))
      .catch((e) => con && datLoi(e instanceof LoiApi ? e.message : 'Không đọc được sổ kho.'));
    return () => {
      con = false;
    };
  }, [sp.id]);

  return (
    <HopHoi
      tieuDe={`Sổ kho — ${sp.sku}`}
      nutY="Đóng"
      dangLam={false}
      dong={dong}
      dongY={dong}
    >
      <DaiLoi loi={loi} />
      <div style={{ marginBottom: 14, fontSize: 'var(--chu-nho)' }}>
        <b>{sp.ten}</b> — trên kệ trừ phần các giỏ đang giữ, còn bán{' '}
        <b className="so">{sp.con_ban}</b> {sp.don_vi}.
      </div>
      {ds === null && !loi && <div className="xuong" style={{ height: 120 }} />}
      {ds?.length === 0 && (
        <div style={{ color: 'var(--chu-3)', padding: '20px 0' }}>Chưa có bút toán nào.</div>
      )}
      {(ds ?? []).map((d) => (
        <div
          key={d.id}
          className="noi-nho"
          style={{ padding: '11px 14px', marginBottom: 9, display: 'flex', gap: 12, alignItems: 'baseline' }}
        >
          <span style={{ fontWeight: 700, fontSize: 12, minWidth: 96 }}>
            {TEN_LOAI[d.loai] ?? d.loai}
          </span>
          <span
            className="so"
            style={{
              minWidth: 46,
              fontWeight: 700,
              color: d.thay_doi_tren_ke > 0 ? 'var(--tot)' : d.thay_doi_tren_ke < 0 ? 'var(--nhan)' : 'var(--chu-3)',
            }}
          >
            {d.thay_doi_tren_ke > 0 ? '+' : ''}
            {d.thay_doi_tren_ke || '—'}
          </span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--chu-2)' }}>
            {d.ghi_chu ?? '—'}
            {d.boi && <span style={{ color: 'var(--chu-3)' }}> · {d.boi}</span>}
          </span>
          <span style={{ fontSize: 11, color: 'var(--chu-3)', flexShrink: 0 }}>
            {new Date(d.luc).toLocaleString('vi-VN', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      ))}
    </HopHoi>
  );
}
